/*
 * ecard.ts — LINE LIFF glue for the shareable e-card (/ecard). Its own bundle
 * (wwwroot/js/dist/ecard.js), loaded only by _ECardLayout.cshtml.
 *
 * The countdown / reveal on the page are handled by the main site.js bundle.
 * This module only wires the two share controls:
 *
 *   [data-ecard-share-line]  Sends the Flex Message via liff.shareTargetPicker(),
 *   which needs a real SSO login session AND a fresh user gesture (it opens a
 *   popup). isApiAvailable() alone is NOT enough — it can be true even where
 *   there is no session — so canShareTarget() also checks isInClient()/
 *   isLoggedIn(). What the next tap does:
 *       - Session ready (LIFF browser via https://liff.line.me/{id}, in-client,
 *         or back from a login) -> button reads "Share via LINE"; one tap opens
 *         the picker.
 *       - Inside LINE but no session / picker not available -> we NEVER navigate
 *         (liff.login() -> access.line.me and social-plugins.line.me/lineit/share
 *         both render BLANK inside LINE's in-app WebView, iOS especially). We use
 *         an in-page share instead: navigator.share() -> clipboard copy.
 *       - Plain external browser, no session -> button reads "Log in to share via
 *         LINE"; the tap is a liff.login() round-trip (redirectUri carries
 *         ?ecardshare=1). The redirect-return page instance can't reliably open
 *         the picker, so on return we reload once (?ecardrl=1) from the
 *         established session; the button is then "Share via LINE" and focused.
 *       - No LIFF configured -> navigator.share() / clipboard copy.
 *
 *   [data-ecard-share]  "Share this invitation" — standard link share, NEVER
 *   touches LINE / LIFF and never logs anyone in:
 *       - navigator.share({ url })      (native sheet — OG card renders on arrival)
 *       - else copy the link to the clipboard (button flashes a confirmation)
 *       - else fall through to the <a href> mailto: link (works with no JS)
 *
 * shareTargetPicker needs the LIFF app's "share target picker" feature turned on
 * in the LINE console. A LIFF init failure must never break the page — every LIFF
 * call is guarded.
 */

interface ECardData {
  liffId: string | null;
  liffUrl: string | null;
  shareUrl: string;
  detailsUrl: string;
  flexMessage: unknown;
}

// The LIFF SDK attaches itself to window as `liff`; we only touch a tiny surface.
interface Liff {
  init(config: { liffId: string }): Promise<void>;
  isLoggedIn(): boolean;
  isInClient(): boolean;
  login(config?: { redirectUri?: string }): void;
  isApiAvailable(name: string): boolean;
  shareTargetPicker(messages: unknown[]): Promise<unknown>;
}

declare global {
  interface Window {
    liff?: Liff;
  }
}

// Carried on the URL (survives the LINE Login redirect, unlike sessionStorage
// which a fresh LINE webview drops): "the guest already asked to share — resume
// the flow as soon as we can".
const RETURN_PARAM = "ecardshare";

// Set on the one-shot reload we do straight after a LINE Login round-trip: the
// SDK on the page instance that processed the redirect can't reliably open
// shareTargetPicker (blocked popup / not-ready), so we reload once from the
// established session. This marker stops that reload from looping.
const RELOAD_DONE_PARAM = "ecardrl";

// Best-effort "this is an in-app WebView, not a real top-level browser" test.
// A liff.login() / lineit-share navigation renders BLANK in these, so we must
// never start one here. iOS LINE scrubbed the "Line/x.x.x" UA token years ago,
// so the reliable tells there are: it's an iPhone/iPad, AppleWebKit, but has
// neither the "Safari/" token (real Safari) nor a known iOS browser token
// (Chrome/Firefox/Edge for iOS). liff.isInClient() is OR'd in at the call site.
function inAppWebView(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/\bLine\/\d/i.test(ua)) return true; // LINE Android (iOS strips this)
  if (/\bFBAN\/|\bFBAV\/|\bInstagram\b|\bMessenger\b|\bMicroMessenger\b/i.test(ua)) return true;
  if (/;\s*wv\)/.test(ua)) return true; // Android System WebView
  if (
    /(iPhone|iPad|iPod)/.test(ua) &&
    /AppleWebKit/.test(ua) &&
    !/Safari\//.test(ua) &&
    !/(CriOS|FxiOS|EdgiOS|OPiOS)\//.test(ua)
  ) {
    return true; // iOS WKWebView host (LINE, and other in-app browsers)
  }
  return false;
}

function readData(): ECardData | null {
  const el = document.getElementById("ecard-data");
  if (!el || !el.textContent) return null;
  try {
    return JSON.parse(el.textContent) as ECardData;
  } catch {
    return null;
  }
}

function withReturnParam(url: string): string {
  try {
    const u = new URL(url, window.location.href);
    u.searchParams.set(RETURN_PARAM, "1");
    return u.toString();
  } catch {
    return url + (url.includes("?") ? "&" : "?") + RETURN_PARAM + "=1";
  }
}

// Strip one of our own params from the address bar so a manual refresh doesn't
// re-fire the flow. LIFF has already cleaned its own params by the time init()
// resolves.
function consumeParam(name: string): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get(name) !== "1") return false;
  params.delete(name);
  const qs = params.toString();
  try {
    history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? "?" + qs : "") + window.location.hash,
    );
  } catch {
    /* replaceState unavailable — harmless, the param just lingers */
  }
  return true;
}

async function initEcard(): Promise<void> {
  const data = readData();
  if (!data) return;

  const liff = window.liff;
  let liffReady = false;

  if (data.liffId && liff) {
    try {
      await liff.init({ liffId: data.liffId });
      liffReady = true;
    } catch {
      liffReady = false;
    }
  }

  const safeIsInClient = (): boolean => {
    try {
      return !!liff && liff.isInClient();
    } catch {
      return false;
    }
  };
  const safeIsLoggedIn = (): boolean => {
    try {
      return !!liff && liff.isLoggedIn();
    } catch {
      return false;
    }
  };

  // Running inside the LINE app's WebView (in-client LIFF app, or a plain link
  // tapped in a chat), or any other in-app WebView. We must never navigate away
  // for sharing here: access.line.me and social-plugins.line.me both render blank
  // in LINE's in-app WebView (iOS especially).
  const inLine = (): boolean => safeIsInClient() || inAppWebView();

  // shareTargetPicker needs a real LINE session, NOT just the feature flag:
  // isApiAvailable() alone can be true with no SSO session, where calling the
  // picker throws. Require an actual session on top of it.
  const canShareTarget = (): boolean =>
    liffReady &&
    !!liff &&
    liff.isApiAvailable("shareTargetPicker") &&
    (safeIsInClient() || safeIsLoggedIn());

  const lineBtn = document.querySelector<HTMLAnchorElement>("[data-ecard-share-line]");
  const shareBtn = document.querySelector<HTMLAnchorElement>("[data-ecard-share]");
  const hint = document.querySelector<HTMLElement>("[data-ecard-share-hint]");

  let copyTimer: ReturnType<typeof setTimeout> | undefined;
  const flashCopied = (btn: HTMLElement): void => {
    const copied = btn.getAttribute("data-copied-label");
    const original = btn.getAttribute("data-share-label") ?? btn.textContent;
    if (!copied || original == null) return;
    if (copyTimer === undefined) btn.setAttribute("data-share-label", original);
    btn.textContent = copied;
    clearTimeout(copyTimer);
    copyTimer = setTimeout(() => {
      btn.textContent = btn.getAttribute("data-share-label");
      copyTimer = undefined;
    }, 2200);
  };

  // Fallback hint, shown when there is nothing better to offer.
  const nudge = (): void => {
    if (hint) hint.hidden = false;
    lineBtn?.focus();
  };

  interface ShareNavigator {
    share?: (data: { url?: string; title?: string; text?: string }) => Promise<void>;
    clipboard?: { writeText?: (text: string) => Promise<void> };
  }
  const nav = (typeof navigator === "undefined" ? undefined : navigator) as
    | ShareNavigator
    | undefined;

  // In-page share, safe everywhere (never navigates): native share sheet, else
  // copy the link with a visible "copied" flash, else the hint.
  const softShare = async (): Promise<void> => {
    if (nav?.share) {
      try {
        await nav.share({ url: data.shareUrl, title: document.title });
      } catch {
        /* cancelled / unsupported */
      }
      return;
    }
    if (nav?.clipboard?.writeText) {
      try {
        await nav.clipboard.writeText(data.shareUrl);
        if (lineBtn) flashCopied(lineBtn);
        else nudge();
        return;
      } catch {
        /* fall through to the hint */
      }
    }
    nudge();
  };

  // Tap-driven: a user gesture is in hand. A refused picker falls back to an
  // in-page share inside LINE, or the LINE web-share popup in an outside browser.
  const openPicker = (): void => {
    liff!.shareTargetPicker([data.flexMessage]).catch(() => {
      if (inLine()) {
        void softShare();
      } else if (lineBtn) {
        window.open(lineBtn.href, "_blank", "noopener");
      } else {
        nudge();
      }
    });
  };

  // ---- "Share via LINE" — Flex Message; the only place a LINE login may start ----

  // Only a plain external browser needs the "log in first" label. Inside LINE, or
  // once a session exists, the button reads "Share via LINE".
  const needsLogin = (): boolean =>
    liffReady && !!liff && !canShareTarget() && !safeIsLoggedIn() && !inLine();

  const applyLineBtnLabel = (): void => {
    if (!lineBtn) return;
    const share = lineBtn.getAttribute("data-label-share");
    const login = lineBtn.getAttribute("data-label-login");
    const next = needsLogin() ? login : share;
    if (next && lineBtn.textContent !== next) lineBtn.textContent = next;
  };
  applyLineBtnLabel();

  // Back from a LINE Login round-trip (external browser only — we never start one
  // inside LINE). Never open the picker programmatically here: with no user
  // gesture the browser blocks its popup, and the SDK on the redirect-return page
  // instance can't reliably open it anyway (the manual "reload after login"
  // workaround). Reload once from the now-established session; the button then
  // reads "Share via LINE" and is focused, so the next tap opens the picker.
  const askedToShare = consumeParam(RETURN_PARAM);
  const reloadedForShare = consumeParam(RELOAD_DONE_PARAM);

  if (
    lineBtn &&
    askedToShare &&
    !reloadedForShare &&
    canShareTarget() &&
    !safeIsInClient()
  ) {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set(RELOAD_DONE_PARAM, "1");
      window.location.replace(u.toString());
      return;
    } catch {
      /* replace unavailable — fall through */
    }
  }

  if (lineBtn && (askedToShare || reloadedForShare) && canShareTarget()) {
    lineBtn.focus();
  }

  lineBtn?.addEventListener("click", (ev: MouseEvent) => {
    // Session ready (LIFF browser, in-client, or back from login): open the picker.
    if (canShareTarget()) {
      ev.preventDefault();
      openPicker();
      return;
    }
    // Plain external browser, LIFF configured, no session yet: this tap is the
    // LINE Login round-trip (redirectUri carries ?ecardshare=1; on return we
    // reload once, then the guest taps again to share). We deliberately do NOT
    // do this inside LINE — access.line.me renders blank in LINE's WebView.
    if (liffReady && liff && !inLine() && !safeIsLoggedIn()) {
      ev.preventDefault();
      try {
        liff.login({ redirectUri: withReturnParam(window.location.href) });
        return;
      } catch {
        /* login unavailable — fall through */
      }
    }
    // Inside LINE, or no LIFF at all: never navigate (that blank page is exactly
    // a navigation to access.line.me / lineit/share). Share in place instead.
    ev.preventDefault();
    void softShare();
  });

  // ---- "Share this invitation" — plain link share, no LINE involvement ----------

  shareBtn?.addEventListener("click", async (ev: MouseEvent) => {
    if (nav?.share) {
      ev.preventDefault();
      try {
        await nav.share({ url: data.shareUrl, title: document.title });
      } catch {
        /* cancelled or unsupported — nothing else to do */
      }
      return;
    }
    if (nav?.clipboard?.writeText) {
      ev.preventDefault();
      try {
        await nav.clipboard.writeText(data.shareUrl);
        flashCopied(shareBtn);
      } catch {
        window.location.href = shareBtn.href; // the mailto: fallback
      }
      return;
    }
    // no share APIs at all: let the <a href> mailto: link open normally
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => void initEcard(), { once: true });
} else {
  void initEcard();
}

export {};
